import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  courseEditHasMetadataChangedAtom,
  courseHasUnsavedStationChangeAtom,
  coursePotentialPassengersAtom,
  errorsCrudContentAtomFamily,
  filterOnlyDirty,
  hasKeys,
  ICourseDto,
  ICourseMetadata,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  Log,
  logBoolean,
  SimpleResult,
  simpleResultErrorEmptyResponse,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { useHighlightEdited } from "../use-highlight-edited";

const COURSE_NOT_BOUND_METADATA_FIELDS: Partial<keyof ICourseMetadata>[] = [
  "stationIds",
  "stationsInfo",
  "totalDistance",
  "totalDuration",
];

interface IUseCourseEditArgs {
  /**
   * The form was seeded with a draft the user never typed (bulk "create course from stations"), so every
   * field is a react-hook-form *default* and nothing is dirty. Skip the dirty filter or the whole payload
   * would be stripped down to `metadata`.
   */
  hasPrefilledDraft?: boolean;
}

export const useCourseEdit = ({
  hasPrefilledDraft,
}: IUseCourseEditArgs = {}) => {
  const { t } = useTranslation();
  const {
    data: { isDuplicate, save },
  } = useCrudEditContext<ICourseDto>();

  const {
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useFormContext<ICourseDto>();
  const { setHighlightedRows } = useHighlightEdited("course");
  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily("course"),
  );
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);

  const setCoursePotentialPassengersAtom = useSetRecoilState(
    coursePotentialPassengersAtom,
  );

  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );
  const setCourseHasMetadataChange = useSetRecoilState(
    courseEditHasMetadataChangedAtom,
  );
  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const setLastRouteRebuildAt = useSetRecoilState(lastRouteRebuildAtAtom);

  const prepareFormFieldsData = (
    formValuesSnapshot: Partial<ICourseDto> | undefined,
    isDuplicateForm: boolean | undefined,
  ): Partial<ICourseDto> => {
    if (!formValuesSnapshot) {
      return {};
    }

    const { metadata, ...formValuesSnapshotExcludeMetadata } =
      formValuesSnapshot;

    if (isDuplicateForm || hasPrefilledDraft) {
      return formValuesSnapshotExcludeMetadata;
    }

    const formDataCleaned = filterOnlyDirty({
      formFields: formValuesSnapshotExcludeMetadata as Partial<ICourseDto>,
      dirtyFields,
      skipMetadata: true,
    });
    Log.debug("Course form plain data cleaned for api send:", formDataCleaned);
    return formDataCleaned;
  };

  const mergeFormValuesToMetadata = (
    formValuesSnapshot: Partial<ICourseDto> | undefined,
    metadataComputed: ICourseMetadata,
  ): void => {
    if (
      !formValuesSnapshot?.metadata ||
      !hasKeys(formValuesSnapshot?.metadata)
    ) {
      return;
    }

    for (const metadataKey in formValuesSnapshot?.metadata) {
      if (
        COURSE_NOT_BOUND_METADATA_FIELDS.includes(
          metadataKey as keyof ICourseMetadata,
        )
      ) {
        continue;
      }

      const metadataValue =
        formValuesSnapshot.metadata[metadataKey as keyof ICourseMetadata];

      if (metadataValue !== undefined) {
        (metadataComputed as Record<string, unknown>)[
          metadataKey as keyof ICourseMetadata
        ] = formValuesSnapshot.metadata[metadataKey as keyof ICourseMetadata];
      }
    }
  };

  const saveCourse = useEvent(
    async (courseDto: ICourseDto): Promise<SimpleResult<ICourseDto>> => {
      setCrudServerErrors(undefined);
      setApiServerErrors(undefined);
      const isFormValid = await trigger();

      if (!isFormValid) {
        return new SimpleResult({
          error: t("errors.formNotValidSkipSavingCourse"),
          debugInfo: dirtyFields,
        });
      }

      const isEdit = Boolean(courseDto.id);

      const formValuesSnapshot = getValues() as Partial<ICourseDto> | undefined;

      Log.debug(
        `Course form values snapshot (is edit:${logBoolean(
          isEdit,
        )}, is duplicate:${logBoolean(isDuplicate)}):`,
        formValuesSnapshot,
      );
      const metadataComputed = { ...courseDto?.metadata };
      mergeFormValuesToMetadata(formValuesSnapshot, metadataComputed);
      Log.debug(
        "Course form metadata composed with form values:",
        metadataComputed,
      );

      const formDataCleaned = prepareFormFieldsData(
        formValuesSnapshot,
        isDuplicate,
      );

      const courseForApiSend: Partial<ICourseDto> = {
        ...formDataCleaned,
        metadata: metadataComputed,
      };

      Log.debug("Course composed data for api:", courseForApiSend);

      const courseApiResponse = await save?.(courseForApiSend);
      Log.debug("Updated course from api:", courseApiResponse);

      if (!courseApiResponse) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!courseApiResponse?.success) {
        return courseApiResponse;
      }

      if (courseApiResponse.data?.id) {
        setHighlightedRows([courseApiResponse.data?.id]);
      }

      setCourseHasUnsavedStationChange(false);
      setCourseHasMetadataChange(false);

      return courseApiResponse;
    },
  );

  const cleanupCourseAtoms = useEvent(() => {
    setCoursePotentialPassengersAtom(undefined);
    setCourseHasUnsavedStationChange(false);
    setCourseHasMetadataChange(false);
    setLastSelectedRebuildType(null);
    setLastRouteRebuildAt(null);
  });
  return { saveCourse, cleanupCourseAtoms };
};
